import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Loader2, FileText, Link2, Paperclip, ShieldCheck } from "lucide-react";
import { errorText } from "@/lib/api";
import { useAsk } from "@/components/admin/AskDialog";
import { CoursePicker, call, inp } from "./AdminLms";

const pdf = (m) => /\.pdf$/i.test(m.filename || "");

export const Materials = () => {
  const ask = useAsk();
  const [course, setCourse] = useState("prompt");
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", url: "", file: null });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => call("get", `/admin/lms/materials?course=${course}`).then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => toast.error(errorText(e))), [course]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!f.file && !f.url) return toast.error("Choose a file or paste a link");
    setBusy(true);
    try {
      let file = {};
      if (f.file) {
        const fd = new FormData(); fd.append("course", course); fd.append("file", f.file);
        const d = await call("post", "/admin/lms/upload", fd);
        file = { file_id: d.file_id, filename: d.filename };
      }
      await call("post", "/admin/lms/materials", { course, title: f.title || f.file?.name || "Material", url: f.file ? null : f.url, ...file });
      toast.success("Added. Students can see it now.");
      setF({ title: "", url: "", file: null }); e.target.reset(); load();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const del = async (m) => {
    if (!(await ask({ title: "Remove this material?", description: `"${m.title}" will disappear from students' portals.`, confirmLabel: "Remove", danger: true }))) return;
    try { await call("delete", `/admin/lms/materials/${m.id}`); load(); } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="space-y-4">
      <CoursePicker value={course} onChange={setCourse} />
      <form data-testid="material-form" onSubmit={add} className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <input data-testid="material-title" placeholder="Title, e.g. Module 3 slides: Advanced prompting" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} />
        <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <label className="flex h-10 cursor-pointer items-center gap-2 truncate rounded-lg border border-dashed border-slate-300 px-3 text-[14px] text-slate-700">
            <Upload className="h-4 w-4 shrink-0 text-[#0E9384]" />{f.file ? f.file.name : "Upload a file (max 25 MB)"}
            <input data-testid="material-file" type="file" className="hidden" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null, url: "" })} />
          </label>
          <span className="text-center text-[12px] text-slate-500">or</span>
          <input data-testid="material-url" placeholder="https:// link (video, notebook, article)" value={f.url} disabled={Boolean(f.file)} onChange={(e) => setF({ ...f, url: e.target.value })} className={inp} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-[12.5px] text-slate-600"><ShieldCheck className="h-4 w-4 text-[#0E9384]" />PDFs are view-only in the portal and watermarked with each student's name and email. Export slides as PDF to protect them.</p>
          <button data-testid="material-add" disabled={busy} className="btn-teal flex items-center gap-1.5 rounded-lg px-4 py-2.5 font-semibold disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add for students</button>
        </div>
      </form>
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {rows.map((m) => {
          const I = m.url ? Link2 : pdf(m) ? FileText : Paperclip;
          return (
            <div key={m.id} data-testid="material-row" className="flex items-center gap-3 p-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-teal-50"><I className="h-5 w-5 text-[#0E9384]" /></span>
              <div className="min-w-0 flex-1"><p className="truncate font-semibold text-[#0B1B33]">{m.title}</p>
                <p className="truncate text-[12.5px] text-slate-500">{m.url ? m.url : pdf(m) ? `View-only PDF · ${m.filename}` : `Downloadable · ${m.filename}`} · {new Date(m.created_at).toLocaleDateString()}</p></div>
              <button onClick={() => del(m)} className="rounded-lg border border-slate-300 p-2 text-slate-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
        {!rows.length && <p className="p-10 text-center text-slate-600">No study material for this course yet.</p>}
      </div>
    </div>
  );
};
